import { useEffect, useState } from 'react';
import { useAnalyticsStore } from '../stores/analyticsStore';
import { useTrafficStore } from '../stores/trafficStore';
import { useRecommendationsStore } from '../stores/recommendationsStore';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ActivationFunnel } from '../components/ActivationFunnel';
import { AdMonetizationFunnel } from '../components/AdMonetizationFunnel';

const UsersIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChatIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V7C3 6.46957 3.21071 5.96086 3.58579 5.58579C3.96086 5.21071 4.46957 5 5 5H19C19.5304 5 20.0391 5.21071 20.4142 5.58579C20.7893 5.96086 21 6.46957 21 7V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const StarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const FeedbackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V7C3 6.46957 3.21071 5.96086 3.58579 5.58579C3.96086 5.21071 4.46957 5 5 5H19C19.5304 5 20.0391 5.21071 20.4142 5.58579C20.7893 5.96086 21 6.46957 21 7V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const RetentionIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M16 3L20 7L16 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 7H13C10.7909 7 9 8.79086 9 11V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 21L4 17L8 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4 17H11C12.8504 17 14.4183 15.7476 14.874 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
    <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrafficIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 2L14.5 8L21 9L16 14L17 21L12 18L7 21L8 14L3 9L9.5 8L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const FunnelIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MetricCard = ({ title, value, subValue, icon: Icon, trend }) => (
  <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="text-text-secondary">
        <Icon />
      </div>
      {trend && (
        <span className={`text-sm font-medium ${trend > 0 ? 'text-success' : 'text-error'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <div className="text-3xl font-bold text-text-primary mb-1">{value}</div>
    <div className="text-sm text-text-secondary">{title}</div>
    {subValue && <div className="text-xs text-text-muted mt-2">{subValue}</div>}
  </div>
);

const PeriodSelector = ({ period, onChange }) => {
  const periods = [
    { value: '7days', label: '7 дней' },
    { value: '30days', label: '30 дней' },
    { value: '90days', label: '90 дней' },
  ];

  return (
    <div className="flex gap-2">
      {periods.map((p) => (
        <button
          key={p.value}
          onClick={() => onChange(p.value)}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            period === p.value
              ? 'bg-accent-primary text-white'
              : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
};

const ActiveUsersPeriodSelector = ({ period, onChange }) => {
  const periods = [
    { value: '24h', label: '24ч' },
    { value: '7days', label: '7 дней' },
    { value: '30days', label: '30 дней' },
    { value: 'all', label: 'Всё время' },
  ];

  return (
    <div className="flex gap-2">
      {periods.map((p) => (
        <button
          key={p.value}
          onClick={() => onChange(p.value)}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
            period === p.value
              ? 'bg-accent-primary text-white'
              : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
};

// Компонент воронки конверсии
const ConversionFunnel = ({ funnel }) => {
  if (!funnel) return null;

  const stages = [
    { label: 'Получили награду', key: 'total_recommended', color: '#8b5cf6' },
    { label: 'Создали чат', key: 'created_chat', color: '#7c3aed' },
    { label: 'Отправили сообщение', key: 'sent_message', color: '#6d28d9' },
    { label: 'Просмотрели рекламу', key: 'viewed_ad', color: '#5b21b6' },
    { label: 'Получили бонус', key: 'claimed_bonus', color: '#4c1d95' },
    { label: 'Активны 7 дней', key: 'active_7d', color: '#3b0764' },
  ];

  const maxValue = funnel.total_recommended;

  return (
    <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="text-accent-primary">
          <FunnelIcon />
        </div>
        <h2 className="text-xl font-bold text-text-primary">Воронка конверсии рекомендаций</h2>
      </div>

      <div className="space-y-4">
        {stages.map((stage, index) => {
          const data = stage.key === 'total_recommended' 
            ? { count: funnel[stage.key], conversion: 100 }
            : funnel[stage.key];
          const count = data?.count || 0;
          const conversion = data?.conversion || 0;
          const width = maxValue > 0 ? (count / maxValue) * 100 : 0;

          return (
            <div key={stage.key}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-text-secondary">{stage.label}</span>
                <div className="text-right">
                  <span className="text-sm font-semibold text-text-primary">{count}</span>
                  <span className="text-xs text-text-muted ml-2">({conversion}%)</span>
                </div>
              </div>
              <div className="w-full bg-bg-tertiary rounded-full h-3 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${width}%`, backgroundColor: stage.color }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-bg-tertiary rounded-lg">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-text-muted mb-1">Активны 30 дней</div>
            <div className="text-lg font-bold text-text-primary">
              {funnel.active_30d?.count || 0} 
              <span className="text-sm text-text-muted ml-1">({funnel.active_30d?.conversion || 0}%)</span>
            </div>
          </div>
          <div>
            <div className="text-xs text-text-muted mb-1">Общая конверсия (30д)</div>
            <div className="text-lg font-bold text-success">
              {funnel.active_30d?.conversion || 0}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Dashboard = () => {
  const { summary, charts, models, retention, hourlyActivity, activeUsers, activationFunnel, adMonetizationFunnel, providerStats, providerChartData, fetchSummary, fetchCharts, fetchModelStats, fetchRetention, fetchHourlyActivity, fetchActiveUsers, fetchActivationFunnel, fetchAdMonetizationFunnel, fetchProviderStats, fetchProviderChart, isLoading, error } = useAnalyticsStore();
  const { dashboardSummary: trafficSummary, fetchDashboardSummary } = useTrafficStore();
  const { funnel: recommendationFunnel, isLoadingFunnel: recommendationIsLoadingFunnel, fetchFunnel: recommendationFetchFunnel } = useRecommendationsStore();
  const [period, setPeriod] = useState('7days');
  const [activeUsersPeriod, setActiveUsersPeriod] = useState('7days');
  const [activationFunnelPeriod, setActivationFunnelPeriod] = useState('30days');
  const [adMonetizationFunnelPeriod, setAdMonetizationFunnelPeriod] = useState('30days');

  useEffect(() => {
    const loadData = async () => {
      console.log('Dashboard: Loading data...');
      await Promise.all([
        fetchSummary(),
        fetchCharts(period),
        fetchModelStats(),
        fetchRetention(),
        fetchHourlyActivity(),
        fetchDashboardSummary(),
        fetchActiveUsers(activeUsersPeriod),
        fetchActivationFunnel(activationFunnelPeriod),
        fetchAdMonetizationFunnel(adMonetizationFunnelPeriod),
        fetchProviderStats(),
        fetchProviderChart(period),
        recommendationFetchFunnel(),
      ]);
      console.log('Dashboard: Data loaded');
    };
    loadData();
  }, [period, activeUsersPeriod, activationFunnelPeriod, adMonetizationFunnelPeriod]);

  console.log('Dashboard render:', { summary, charts, models, retention, hourlyActivity, isLoading, error });

  const formatChartData = (data) => {
    if (!data) return [];
    return data.map((item) => ({
      ...item,
      date: new Date(item.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }),
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-primary">Дашборд</h1>
        <PeriodSelector period={period} onChange={setPeriod} />
      </div>

      {/* Metric Cards */}
      {error && (
        <div className="bg-error/10 border border-error rounded-xl p-4 text-error">
          <p className="font-medium">Ошибка загрузки данных</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Пользователей"
          value={summary?.users?.total ?? '—'}
          subValue={`+${summary?.users?.new_today ?? 0} сегодня`}
          icon={UsersIcon}
        />
        <MetricCard
          title="Чатов"
          value={summary?.chats?.total ?? '—'}
          subValue={`${summary?.chats?.today ?? 0} за 24ч`}
          icon={ChatIcon}
        />
        <MetricCard
          title="Звёзд в системе"
          value={summary?.stars?.total_balance ?? '—'}
          subValue={`${summary?.stars?.rewards_today ?? 0} начислено за 24ч`}
          icon={StarIcon}
        />
        <MetricCard
          title="Retention (7д)"
          value={retention?.active_rate_7d != null ? `${retention.active_rate_7d}%` : '—'}
          subValue={`${retention?.active_7d ?? 0} активных за 7 дней`}
          icon={RetentionIcon}
        />
      </div>

      {/* Дополнительная строка метрик */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <MetricCard
          title="Активные за 30 дней"
          value={retention?.active_rate_30d != null ? `${retention.active_rate_30d}%` : '—'}
          subValue={`${retention?.active_30d ?? 0} пользователей`}
          icon={UsersIcon}
        />
        <MetricCard
          title="Retention когорты"
          value={retention?.retention_7d != null ? `${retention.retention_7d}%` : '—'}
          subValue={`${retention?.retained_new ?? 0} из ${retention?.cohort_size ?? 0} новых`}
          icon={RetentionIcon}
        />
        <MetricCard
          title="Отзывов"
          value={summary?.feedback?.total ?? '—'}
          subValue={`${summary?.feedback?.unread ?? 0} непрочитанных`}
          icon={FeedbackIcon}
        />
        <MetricCard
          title="Источники трафика"
          value={trafficSummary ? `${trafficSummary.ref?.count ?? 0} / ${trafficSummary.hash?.count ?? 0} / ${trafficSummary.direct?.count ?? 0}` : '—'}
          subValue={trafficSummary ? `VK: ${trafficSummary.ref?.percentage ?? 0}% | Hash: ${trafficSummary.hash?.percentage ?? 0}% | Direct: ${trafficSummary.direct?.percentage ?? 0}%` : 'Загрузка...'}
          icon={TrafficIcon}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Registrations Chart */}
        <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Регистрации</h3>
          <div className="h-64">
            {charts?.registrations ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={formatChartData(charts.registrations)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date" stroke="#6b6b6b" fontSize={12} />
                  <YAxis stroke="#6b6b6b" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                    labelStyle={{ color: '#ececec' }}
                  />
                  <Line type="monotone" dataKey="count" stroke="#10a37f" strokeWidth={2} dot={{ fill: '#10a37f' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-text-secondary">
                {isLoading ? <div className="animate-spin h-6 w-6 border-2 border-accent-primary border-t-transparent rounded-full" /> : 'Нет данных'}
              </div>
            )}
          </div>
        </div>

        {/* Messages Chart */}
        <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Сообщения</h3>
          <div className="h-64">
            {charts?.messages ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={formatChartData(charts.messages)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date" stroke="#6b6b6b" fontSize={12} />
                  <YAxis stroke="#6b6b6b" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                    labelStyle={{ color: '#ececec' }}
                  />
                  <Bar dataKey="count" fill="#10a37f" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-text-secondary">
                {isLoading ? <div className="animate-spin h-6 w-6 border-2 border-accent-primary border-t-transparent rounded-full" /> : 'Нет данных'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hourly Activity Chart */}
      <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <ClockIcon />
          Пиковые часы активности
        </h3>
        <div className="h-64">
          {hourlyActivity && hourlyActivity.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyActivity}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="label" stroke="#6b6b6b" fontSize={10} interval={2} />
                <YAxis stroke="#6b6b6b" fontSize={12} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                  labelStyle={{ color: '#ececec' }}
                  formatter={(value) => [`${value} сообщений`, 'Активность']}
                />
                <Bar dataKey="count" fill="#10a37f" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-text-secondary">
              {isLoading ? <div className="animate-spin h-6 w-6 border-2 border-accent-primary border-t-transparent rounded-full" /> : 'Нет данных'}
            </div>
          )}
        </div>
      </div>

      {/* Top Models */}
      <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Популярные модели</h3>
        {models && models.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {models.map((model, index) => (
              <div key={model.model} className="text-center">
                <div className="text-2xl font-bold text-text-primary mb-1">{model.count}</div>
                <div className="text-sm text-text-secondary truncate">{model.model}</div>
                <div className="text-xs text-text-muted">#{index + 1}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-text-secondary">
            {isLoading ? <div className="animate-spin h-6 w-6 border-2 border-accent-primary border-t-transparent rounded-full" /> : 'Нет данных'}
          </div>
        )}
      </div>

      {/* Аналитика провайдеров */}
      <div className="bg-bg-secondary rounded-xl border border-border-color p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Аналитика провайдеров</h3>
        {providerStats && providerStats.providers && providerStats.providers.length > 0 ? (
          <div className="space-y-4">
            {/* Карточки провайдеров */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {providerStats.providers.map((p) => {
                const providerNames = { picoapps: 'Провайдер 1', aikit: 'Провайдер 2', blackbox: 'Провайдер 3' };
                const providerIcons = { picoapps: '⚡', aikit: '🧠', blackbox: '🟦' };
                const name = providerNames[p.provider] || p.provider;
                const icon = providerIcons[p.provider] || '📊';
                return (
                  <div key={p.provider} className="bg-bg-tertiary rounded-lg p-4 text-center">
                    <div className="text-2xl mb-1">{icon}</div>
                    <div className="text-xl font-bold text-text-primary">{p.count}</div>
                    <div className="text-sm text-text-secondary">{name}</div>
                    <div className="text-xs text-text-muted mt-1">{p.percentage}%</div>
                  </div>
                );
              })}
              <div className="bg-bg-tertiary rounded-lg p-4 text-center">
                <div className="text-2xl mb-1">📊</div>
                <div className="text-xl font-bold text-text-primary">{providerStats.total}</div>
                <div className="text-sm text-text-secondary">Всего</div>
                <div className="text-xs text-text-muted mt-1">сообщений</div>
              </div>
            </div>

            {/* Stacked bar chart */}
            {providerChartData && providerChartData.length > 0 && (
              <div className="mt-4">
                <div className="text-sm text-text-secondary mb-2">Сообщения по дням</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={formatChartData(providerChartData)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#6b6b6b" fontSize={12} />
                    <YAxis stroke="#6b6b6b" fontSize={12} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                      labelStyle={{ color: '#ececec' }}
                    />
                    <Bar dataKey="picoapps" stackId="a" fill="#f89148" name="Провайдер 1" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="aikit" stackId="a" fill="#c541d5" name="Провайдер 2" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="blackbox" stackId="a" fill="#417bd5" name="Провайдер 3" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        ) : (
          <div className="text-text-secondary">
            {isLoading ? <div className="animate-spin h-6 w-6 border-2 border-accent-primary border-t-transparent rounded-full" /> : 'Нет данных. Провайдеры учитываются только для assistant-сообщений.'}
          </div>
        )}
      </div>

      {/* Самые активные пользователи */}
      <div className="bg-bg-secondary rounded-xl border border-border-color">
        <div className="p-6 border-b border-border-color flex items-center justify-between flex-wrap gap-4">
          <h3 className="text-lg font-semibold text-text-primary">Самые активные пользователи</h3>
          <ActiveUsersPeriodSelector period={activeUsersPeriod} onChange={setActiveUsersPeriod} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-bg-tertiary border-b border-border-color">
              <tr>
                <th className="text-left text-xs font-medium text-text-secondary px-6 py-3 w-12">#</th>
                <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">VK ID</th>
                <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Сообщения</th>
                <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Чаты</th>
                <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Изображения</th>
                <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Рейтинг</th>
                <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Баланс</th>
                <th className="text-left text-xs font-medium text-text-secondary px-6 py-3">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-color">
              {activeUsers && activeUsers.length > 0 ? (
                activeUsers.map((user, index) => (
                  <tr key={user.vk_user_id} className="hover:bg-bg-tertiary/50 transition-colors">
                    <td className="px-6 py-3 text-sm text-text-muted font-bold">
                      {index + 1}
                    </td>
                    <td className="px-6 py-3 text-sm text-text-primary font-mono">
                      {user.vk_user_id}
                    </td>
                    <td className="px-6 py-3 text-sm text-text-secondary">
                      {user.message_count}
                    </td>
                    <td className="px-6 py-3 text-sm text-text-secondary">
                      {user.chat_count}
                    </td>
                    <td className="px-6 py-3 text-sm text-text-secondary">
                      {user.image_count}
                    </td>
                    <td className="px-6 py-3 text-sm font-bold text-accent-primary">
                      {user.activity_score}
                    </td>
                    <td className="px-6 py-3 text-sm text-text-primary">
                      {user.balance} ⭐
                    </td>
                    <td className="px-6 py-3">
                      {user.is_disabled ? (
                        <span className="text-xs px-2 py-1 rounded bg-error/20 text-error border border-error/30">
                          Заблокирован
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded bg-success/20 text-success border border-success/30">
                          Активен
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-text-secondary">
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin h-5 w-5 border-2 border-accent-primary border-t-transparent rounded-full" />
                        Загрузка...
                      </div>
                    ) : (
                      'Нет данных'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Воронка активации новых пользователей */}
      <ActivationFunnel
        funnel={activationFunnel}
        isLoading={isLoading}
        period={activationFunnelPeriod}
        onPeriodChange={setActivationFunnelPeriod}
      />

      {/* Воронка монетизации через рекламу */}
      <AdMonetizationFunnel
        funnel={adMonetizationFunnel}
        isLoading={isLoading}
        period={adMonetizationFunnelPeriod}
        onPeriodChange={setAdMonetizationFunnelPeriod}
      />

      {/* Воронка конверсии рекомендаций */}
      {recommendationIsLoadingFunnel ? (
        <div className="bg-bg-secondary rounded-xl border border-border-color p-6 animate-pulse">
          <div className="h-6 w-48 bg-bg-tertiary rounded mb-6" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i}>
                <div className="h-4 w-32 bg-bg-tertiary rounded mb-2" />
                <div className="h-3 w-full bg-bg-tertiary rounded" />
              </div>
            ))}
          </div>
        </div>
      ) : recommendationFunnel ? (
        <ConversionFunnel funnel={recommendationFunnel} />
      ) : null}
    </div>
  );
};
